import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { IFieldError, interpretDataIntoXlsxFormat, QueryStringBuilder } from "../helpers";
import { ICustomer, IEnumCustomer } from "./customersReducer";
import { IEnumRentalPackage, IRentalPackage } from "./rentalPackagesReducer";
import { IStaff } from "./staffsReducer";
import { getFieldErrors } from "../helpers";
import { IEnumPayment } from "./paymentsReducer";
import { APPROVED, COMPLETED, INITIALIZED, MEMBERSHIP, UNAPPROVED, USER, XLSX } from "../../config/constants";
import { ISite } from "./sitesReducer";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { IUser } from "./authReducer";
import xlsx from "json-as-xlsx";
import { tryConfirmLogin } from "./authReducer";




export interface ILoggedOrder {
    _id: string;
    ref: string;
    customer: ICustomer,
    staff: IStaff,
    rentalPackage: IRentalPackage,
    site: ISite,
    paymentTypes: string[],
    payments: IEnumPayment[],
    status: string,
    approvalTimeStamp: number | null,
    departureTimeStamp: number | null,
    createAt: string;
    updateAt: string;
    deliveryDueDate: string;
    remarks?: string;
    discount?: number;
}

export interface IOrderUpdate {
    _id: string;
    editor: IUser & ICustomer;
    oldData: ILoggedOrder;
    newData: ILoggedOrder;
    orderFields: string[] | null;
    packageFields: string[] | null;
    createdAt: string;
}


export interface IOrder {
    _id: string;
    ref: string;
    creator: IUser & ICustomer,
    customer: ICustomer,
    customerAddress?: string,
    staff: IStaff,
    rentalPackage: IRentalPackage,
    site: ISite,
    paymentTypes: string[],
    payments: IEnumPayment[],
    status: string,
    approvalTimeStamp: number | null,
    departureTimeStamp: number | null,
    updates: IOrderUpdate[];
    createAt: string;
    updateAt: string;
    deliveryDueDate: string;
    remarks?: string;
    discount?: number;
}

export interface IEnumOrder {
    _id: string;
    ref: string;
    customer: IEnumCustomer;
    customerAddress?: string,
    rentalPackage: IEnumRentalPackage;
    updatesCount: number;
    status: string;
    discount?: number;
    createdAt: string;
    updatedAt: string;
}

class RegOrderErrs {
    form: string | null = null;
    customerId: string | null = null;
    staffId: string | null = null;
    rentalItems: string | null = null;
    siteId: string | null = null;
    rentalDaysCount: string | null = null;
    transportationFee: string | null = null;
    paymentTypes: string | null = null;
    bankName: string | null = null;
    accountName: string | null = null;
    accountNumber: string | null = null;
    deliveryDueDate: string | null = null;
    remarks: string | null = null;
    discount: string | null = null;
}

class ExportOrdersHistoryErrs {
    form: string | null = null;
    startTimeStamp: string | null = null;
    endTimeStamp: string | null = null;
}

export interface IOrdersState {
    registeringOrder: boolean;
    registeredOrder: boolean;
    regOrderErrs: RegOrderErrs;
    updatingOrder: boolean;
    updatedOrder: boolean;
    updateOrderErrs: RegOrderErrs;
    fetchingRecentOrders: boolean;
    fetchRecentOrdersErr: string | null;
    recentOrders: IEnumOrder[];
    fetchingUnapprovedOrders: boolean;
    fetchUnapprovedOrdersErr: string | null;
    unapprovedOrders: IEnumOrder[];
    fetchingActiveOrders: boolean;
    fetchActiveOrdersErr: string | null;
    activeOrders: IEnumOrder[];
    fetchingCompletedOrders: boolean;
    fetchCompletedOrdersErr: string | null;
    completedOrders: IEnumOrder[];
    fetchingOrders4Delivery: boolean;
    fetchOrders4DeliveryErr: string | null;
    orders4Delivery: IEnumOrder[];
    fetchingOrders4PickUp: boolean;
    fetchOrders4PickUpErr: string | null;
    orders4PickUp: IEnumOrder[];
    fetchingMainOrder: boolean;
    fetchMainOrderErr: string | null;
    mainOrder: IOrder | null;
    mainOrderId: string | null;
    fetchingActiveMembership: boolean;
    fetchActiveMembershipErr: string | null;
    activeMembership: IOrder | null;
    realActiveMembership: IOrder | null;
    orderApproved: boolean;
    approvingOrder: boolean;
    approveOrderErr: string | null;
    deletingOrder: boolean,
    deletedOrder: boolean,
    deleteOrderErr: string | null;
    assignedDeliveryDriver: boolean;
    assigningDeliveryDriver: boolean;
    assignDeliveryDriverErr: string | null;
    assignedRetrievalDriver: boolean;
    assigningRetrievalDriver: boolean;
    assignRetrievalDriverErr: string | null;
    confirmedOrderCompletion: boolean;
    confirmingOrderCompletion: boolean;
    confirmOderCompletionErr: string | null;
    confirmedOrderDeparture: boolean;
    confirmingOrderDeparture: boolean;
    confirmOderDepartureErr: string | null;
    exportingOrdersHistory: boolean | null;
    exportedOrdersHistory: boolean | null;
    exportOrdersHistoryErrs: ExportOrdersHistoryErrs;
}

const initialState = {
    registeringOrder: false,
    registeredOrder: false,
    regOrderErrs: new RegOrderErrs(),
    updatingOrder: false,
    updatedOrder: false,
    updateOrderErrs: new RegOrderErrs(),
    fetchingRecentOrders: false,
    fetchRecentOrdersErr: null,
    recentOrders: [],
    fetchingUnapprovedOrders: false,
    fetchUnapprovedOrdersErr: null,
    unapprovedOrders: [],
    fetchingActiveOrders: false,
    fetchActiveOrdersErr: null,
    activeOrders: [],
    fetchingCompletedOrders: false,
    fetchCompletedOrdersErr: null,
    completedOrders: [],
    fetchingOrders4Delivery: false,
    fetchOrders4DeliveryErr: null,
    orders4Delivery: [],
    fetchingOrders4PickUp: false,
    fetchOrders4PickUpErr: null,
    orders4PickUp: [],
    fetchingMainOrder: false,
    fetchMainOrderErr: null,
    mainOrderId: null,
    mainOrder: null,
    activeMembership: null,
    realActiveMembership: null,
    fetchingActiveMembership: false,
    fetchActiveMembershipErr: null,
    orderApproved: false,
    approvingOrder: false,
    approveOrderErr: null,
    deletingOrder: false,
    deletedOrder: false,
    deleteOrderErr: null,
    assignedDeliveryDriver: false,
    assigningDeliveryDriver: false,
    assignDeliveryDriverErr: null,
    assignedRetrievalDriver: false,
    assigningRetrievalDriver: false,
    assignRetrievalDriverErr: null,
    confirmedOrderCompletion: false,
    confirmingOrderCompletion: false,
    confirmOderCompletionErr: null,
    confirmedOrderDeparture: false,
    confirmingOrderDeparture: false,
    confirmOderDepartureErr: null,
    exportingOrdersHistory: false,
    exportedOrdersHistory: false,
    exportOrdersHistoryErrs: new ExportOrdersHistoryErrs()

} as IOrdersState

const ordersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        clearRegOrderFieldErr: (state: IOrdersState, action: PayloadAction<string>) => {
            state.regOrderErrs[action.payload as keyof RegOrderErrs] = null;
        },
        clearRegOrderErrs: (state: IOrdersState) => {
            state.regOrderErrs = new RegOrderErrs();
        },
        tryResetRegOrderFormErr: (state: IOrdersState) => {
            let shouldReset = true;

            for (let key in state.regOrderErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.regOrderErrs[key as keyof RegOrderErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.regOrderErrs.form = null;
            }
        },
        resetRegisteredOrder: (state: IOrdersState) => {
            state.registeredOrder = false;
        },
        regOrderStart: (state: IOrdersState) => {
            state.registeringOrder = true;
            state.registeredOrder = false;
        },
        regOrderPass: (state: IOrdersState) => {
            state.registeringOrder = false;
            state.registeredOrder = true;
        },
        regOrderFail: (state: IOrdersState, action: PayloadAction<IFieldError[]>) => {
            state.registeringOrder = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.regOrderErrs) {
                    if (err.field === key) {
                        state.regOrderErrs[key as keyof RegOrderErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.regOrderErrs.form) {
                state.regOrderErrs.form = 'Form incorrectly filled.';
            }
        },
        clearupdateOrderFieldErr: (state: IOrdersState, action: PayloadAction<string>) => {
            state.updateOrderErrs[action.payload as keyof RegOrderErrs] = null;
        },
        clearUpdateOrderErrs: (state: IOrdersState) => {
            state.updateOrderErrs = new RegOrderErrs();
        },
        tryResetUpdateOrderFormErr: (state: IOrdersState) => {
            let shouldReset = true;

            for (let key in state.updateOrderErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.updateOrderErrs[key as keyof RegOrderErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.updateOrderErrs.form = null;
            }
        },
        resetUpdatedOrder: (state: IOrdersState) => {
            state.updatedOrder = false;
        },
        updateOrderStart: (state: IOrdersState) => {
            state.updatedOrder = false;
            state.updateOrderErrs = new RegOrderErrs();
            state.updatingOrder = true;
        },
        updateOrderPass: (state: IOrdersState, action: PayloadAction<IOrder>) => {
            state.updatingOrder = false;
            state.updatedOrder = true;
        },
        updateOrderFail: (state: IOrdersState, action: PayloadAction<IFieldError[]>) => {
            state.updatingOrder = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.updateOrderErrs) {
                    if (err.field === key) {
                        state.updateOrderErrs[key as keyof RegOrderErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.updateOrderErrs.form) {
                state.updateOrderErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchRecentOrdersErr: (state: IOrdersState) => {
            state.fetchRecentOrdersErr = null;
        },
        emptyRecentOrders: (state: IOrdersState) => {
            state.recentOrders = [];
        },
        fetchRecentOrdersStart: (state: IOrdersState) => {
            state.fetchingRecentOrders = true;
            state.fetchRecentOrdersErr = null;
        },
        fetchRecentOrdersPass: (state: IOrdersState, action: PayloadAction<IEnumOrder[]>) => {
            state.fetchingRecentOrders = false;
            state.recentOrders = action.payload;
        },
        fetchRecentOrdersFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.fetchingRecentOrders = false;
            state.fetchRecentOrdersErr = action.payload;
        },
        clearFetchUnapprovedOrdersErr: (state: IOrdersState) => {
            state.fetchUnapprovedOrdersErr = null;
        },
        fetchUnapprovedOrdersStart: (state: IOrdersState) => {
            state.fetchingUnapprovedOrders = true;
            state.fetchUnapprovedOrdersErr = null;
        },
        fetchUnapprovedOrdersPass: (state: IOrdersState, action: PayloadAction<IEnumOrder[]>) => {
            state.fetchingUnapprovedOrders = false;
            state.unapprovedOrders = action.payload;
        },
        fetchUnapprovedOrdersFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.fetchingUnapprovedOrders = false;
            state.fetchUnapprovedOrdersErr = action.payload;
        },
        clearFetchActiveOrdersErr: (state: IOrdersState) => {
            state.fetchActiveOrdersErr = null;
        },
        fetchActiveOrdersStart: (state: IOrdersState) => {
            state.fetchingActiveOrders = true;
            state.fetchActiveOrdersErr = null;
        },
        fetchActiveOrdersPass: (state: IOrdersState, action: PayloadAction<IEnumOrder[]>) => {
            state.fetchingActiveOrders = false;
            state.activeOrders = action.payload;
        },
        fetchActiveOrdersFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.fetchingActiveOrders = false;
            state.fetchActiveOrdersErr = action.payload;
        },
        clearFetchCompletedOrdersErr: (state: IOrdersState) => {
            state.fetchCompletedOrdersErr = null;
        },
        fetchCompletedOrdersStart: (state: IOrdersState) => {
            state.fetchingCompletedOrders = true;
            state.fetchCompletedOrdersErr = null;
        },
        fetchCompletedOrdersPass: (state: IOrdersState, action: PayloadAction<IEnumOrder[]>) => {
            state.fetchingCompletedOrders = false;
            state.completedOrders = action.payload;
        },
        fetchCompletedOrdersFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.fetchingCompletedOrders = false;
            state.fetchCompletedOrdersErr = action.payload;
        },
        clearFetchOrders4DeliveryErr: (state: IOrdersState) => {
            state.fetchOrders4DeliveryErr = null;
        },
        fetchOrders4DeliveryStart: (state: IOrdersState) => {
            state.fetchingOrders4Delivery = true;
            state.fetchOrders4DeliveryErr = null;
        },
        fetchOrders4DeliveryPass: (state: IOrdersState, action: PayloadAction<IEnumOrder[]>) => {
            state.fetchingOrders4Delivery = false;
            state.orders4Delivery = action.payload;
        },
        fetchOrders4DeliveryFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.fetchingOrders4Delivery = false;
            state.fetchOrders4DeliveryErr = action.payload;
        },
        clearFetchOrders4PickUpErr: (state: IOrdersState) => {
            state.fetchOrders4PickUpErr = null;
        },
        fetchOrders4PickUpStart: (state: IOrdersState) => {
            state.fetchingOrders4PickUp = true;
            state.fetchOrders4PickUpErr = null;
        },
        fetchOrders4PickUpPass: (state: IOrdersState, action: PayloadAction<IEnumOrder[]>) => {
            state.fetchingOrders4PickUp = false;
            state.orders4PickUp = action.payload;
        },
        fetchOrders4PickUpFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.fetchingOrders4PickUp = false;
            state.fetchOrders4PickUpErr = action.payload;
        },
        clearFetchMainOrderErr: (state: IOrdersState) => {
            state.fetchMainOrderErr = null;
        },
        fetchMainOrderStart: (state: IOrdersState) => {
            state.fetchingMainOrder = true;
            state.fetchMainOrderErr = null;
        },
        fetchMainOrderPass: (state: IOrdersState, action: PayloadAction<IOrder>) => {
            state.fetchingMainOrder = false;
            state.mainOrder = action.payload;
        },
        fetchMainOrderFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.fetchingMainOrder = false;
            state.fetchMainOrderErr = action.payload;
        },
        clearFetchActiveMembershipErr: (state: IOrdersState) => {
            state.fetchActiveMembershipErr = null;
        },
        setActiveMembership: (state: IOrdersState, action: PayloadAction<IOrder | null>) => {
            state.activeMembership = action.payload;
        },
        resetActiveMembership: (state: IOrdersState) => {
            state.activeMembership = state.realActiveMembership;
        },
        fetchActiveMembershipStart: (state: IOrdersState) => {
            state.fetchingActiveMembership = true;
            state.fetchActiveMembershipErr = null;
        },
        fetchActiveMembershipPass: (state: IOrdersState, action: PayloadAction<IOrder>) => {
            state.fetchingActiveMembership = false;
            state.activeMembership = action.payload;
            state.realActiveMembership = action.payload;
        },
        fetchActiveMembershipFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.fetchingActiveMembership = false;
            state.fetchActiveMembershipErr = action.payload;
        },
        setMainOrderId: (state: IOrdersState, action: PayloadAction<string | null>) => {
            state.mainOrderId = action.payload;
        },
        setMainOrder: (state: IOrdersState, action: PayloadAction<IOrder | null>) => {
            state.mainOrder = action.payload;
        },
        resetMainOrder: (state: IOrdersState) => {
            state.mainOrder = null;
        },
        clearApproveOrderErr: (state: IOrdersState) => {
            state.approveOrderErr = null;
        },
        resetOrderApproved: (state: IOrdersState) => {
            state.orderApproved = false;
        },
        approveOrderStart: (state: IOrdersState) => {
            state.approvingOrder = true;
            state.approveOrderErr = null;
            state.orderApproved = false;
        },
        approveOrderPass: (state: IOrdersState) => {
            state.approvingOrder = false;
            state.orderApproved = true;
        },
        approveOrderFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.approvingOrder = false;
            state.approveOrderErr = action.payload;
        },
        clearDeleteOrderErr: (state: IOrdersState) => {
            state.deleteOrderErr = null;
        },
        resetDeletedOrder: (state: IOrdersState) => {
            state.deletedOrder = false;
        },
        deleteOrderStart: (state: IOrdersState) => {
            state.deletingOrder = true;
            state.deletedOrder = false;
            state.deleteOrderErr = null;
        },
        deleteOrderPass: (state: IOrdersState) => {
            state.deletingOrder = false;
            state.deletedOrder = true;
        },
        deleteOrderFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.deletingOrder = false;
            state.deleteOrderErr = action.payload
        },
        resetAssignDeliveryDriverErr: (state: IOrdersState) => {
            state.assignDeliveryDriverErr = null;
        },
        resetAssignedDeliveryDriver: (state: IOrdersState) => {
            state.assignedDeliveryDriver = false;
        },
        assignDeliveryDriverStart: (state: IOrdersState) => {
            state.assignDeliveryDriverErr = null;
            state.assigningDeliveryDriver = true;
            state.assignedDeliveryDriver = false;
        },
        assignDeliveryDriverPass: (state: IOrdersState) => {
            state.assigningDeliveryDriver = false;
            state.assignedDeliveryDriver = true;
        },
        assignDeliveryDriverFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.assigningDeliveryDriver = false;
            state.assignDeliveryDriverErr = action.payload;
        },
        resetAssignedRetrievalDriver: (state: IOrdersState) => {
            state.assignedRetrievalDriver = false;
        },
        resetAssignRetrievalDriverErr: (state: IOrdersState) => {
            state.assignRetrievalDriverErr = null;
        },
        assignRetrievalDriverStart: (state: IOrdersState) => {
            state.assignRetrievalDriverErr = null;
            state.assigningRetrievalDriver = true;
            state.assignedRetrievalDriver = false;
        },
        assignRetrievalDriverPass: (state: IOrdersState) => {
            state.assigningRetrievalDriver = false;
            state.assignedRetrievalDriver = true;
        },
        assignRetrievalDriverFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.assigningRetrievalDriver = false;
            state.assignRetrievalDriverErr = action.payload;
        },
        clearConfirmOrderCompletionErr: (state: IOrdersState) => {
            state.confirmOderCompletionErr = null;
        },
        resetConfirmedOrderCompletion: (state: IOrdersState) => {
            state.confirmedOrderCompletion = false;
        },
        confirmOrderCompletionStart: (state: IOrdersState) => {
            state.confirmingOrderCompletion = true;
            state.confirmOderCompletionErr = null;
            state.confirmedOrderCompletion = false;
        },
        confirmOrderCompletionPass: (state: IOrdersState) => {
            state.confirmingOrderCompletion = false;
            state.confirmedOrderCompletion = true;
        },
        confirmOrderCompletionFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.confirmingOrderCompletion = false;
            state.confirmOderCompletionErr = action.payload;
        },
        clearConfirmOrderDepartureErr: (state: IOrdersState) => {
            state.confirmOderDepartureErr = null;
        },
        resetConfirmedOrderDeparture: (state: IOrdersState) => {
            state.confirmedOrderDeparture = false;
        },
        confirmOrderDepartureStart: (state: IOrdersState) => {
            state.confirmingOrderDeparture = true;
            state.confirmOderDepartureErr = null;
            state.confirmedOrderDeparture = false;
        },
        confirmOrderDeparturePass: (state: IOrdersState) => {
            state.confirmingOrderDeparture = false;
            state.confirmedOrderDeparture = true;
        },
        confirmOrderDepartureFail: (state: IOrdersState, action: PayloadAction<string>) => {
            state.confirmingOrderDeparture = false;
            state.confirmOderDepartureErr = action.payload;
        },
        clearExportOrdersHistoryFieldErr: (state: IOrdersState, action: PayloadAction<string>) => {
            state.exportOrdersHistoryErrs[action.payload as keyof ExportOrdersHistoryErrs] = null;
        },
        clearExportOrdersHistoryErrs: (state: IOrdersState) => {
            state.exportOrdersHistoryErrs = new ExportOrdersHistoryErrs();
        },
        tryResetExportOrdersHistoryFormErr: (state: IOrdersState) => {
            let shouldReset = true;

            for (let key in state.exportOrdersHistoryErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.exportOrdersHistoryErrs[key as keyof ExportOrdersHistoryErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.exportOrdersHistoryErrs.form = null;
            }
        },
        resetExportedExportOrdersHistory: (state: IOrdersState) => {
            state.exportedOrdersHistory = false;
        },
        exportOrdersHistoryStart: (state: IOrdersState) => {
            state.exportingOrdersHistory = true;
            state.exportedOrdersHistory = false;
        },
        exportOrdersHistoryPass: (state: IOrdersState) => {
            state.exportingOrdersHistory = false;
            state.exportedOrdersHistory = true;
        },
        exportOrdersHistoryFail: (state: IOrdersState, action: PayloadAction<IFieldError[]>) => {
            state.exportingOrdersHistory = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.exportOrdersHistoryErrs) {
                    if (err.field === key) {
                        state.exportOrdersHistoryErrs[key as keyof ExportOrdersHistoryErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.exportOrdersHistoryErrs.form) {
                state.exportOrdersHistoryErrs.form = 'Form incorrectly filled.';
            }
        },
    }
});

export const {
    clearRegOrderFieldErr,
    clearRegOrderErrs,
    tryResetRegOrderFormErr,
    resetRegisteredOrder,
    regOrderStart,
    regOrderPass,
    regOrderFail,
    clearUpdateOrderErrs,
    clearupdateOrderFieldErr,
    tryResetUpdateOrderFormErr,
    resetUpdatedOrder,
    updateOrderStart,
    updateOrderPass,
    updateOrderFail,
    clearFetchRecentOrdersErr,
    emptyRecentOrders,
    fetchRecentOrdersStart,
    fetchRecentOrdersPass,
    fetchRecentOrdersFail,
    clearFetchUnapprovedOrdersErr,
    fetchUnapprovedOrdersStart,
    fetchUnapprovedOrdersPass,
    fetchUnapprovedOrdersFail,
    clearFetchActiveOrdersErr,
    fetchActiveOrdersStart,
    fetchActiveOrdersPass,
    fetchActiveOrdersFail,
    clearFetchCompletedOrdersErr,
    fetchCompletedOrdersStart,
    fetchCompletedOrdersPass,
    fetchCompletedOrdersFail,
    clearFetchOrders4DeliveryErr,
    fetchOrders4DeliveryStart,
    fetchOrders4DeliveryPass,
    fetchOrders4DeliveryFail,
    clearFetchOrders4PickUpErr,
    fetchOrders4PickUpStart,
    fetchOrders4PickUpPass,
    fetchOrders4PickUpFail,
    clearFetchMainOrderErr,
    fetchMainOrderStart,
    fetchMainOrderPass,
    fetchMainOrderFail,
    setActiveMembership,
    resetActiveMembership,
    fetchActiveMembershipStart,
    fetchActiveMembershipPass,
    fetchActiveMembershipFail,
    setMainOrder,
    setMainOrderId,
    resetMainOrder,
    clearApproveOrderErr,
    resetOrderApproved,
    approveOrderStart,
    approveOrderPass,
    approveOrderFail,
    clearDeleteOrderErr,
    resetDeletedOrder,
    deleteOrderStart,
    deleteOrderPass,
    deleteOrderFail,
    resetAssignedDeliveryDriver,
    resetAssignDeliveryDriverErr,
    assignDeliveryDriverStart,
    assignDeliveryDriverPass,
    assignDeliveryDriverFail,
    resetAssignedRetrievalDriver,
    resetAssignRetrievalDriverErr,
    assignRetrievalDriverStart,
    assignRetrievalDriverPass,
    assignRetrievalDriverFail,
    clearConfirmOrderCompletionErr,
    resetConfirmedOrderCompletion,
    confirmOrderCompletionStart,
    confirmOrderCompletionPass,
    confirmOrderCompletionFail,
    clearConfirmOrderDepartureErr,
    resetConfirmedOrderDeparture,
    confirmOrderDepartureStart,
    confirmOrderDeparturePass,
    confirmOrderDepartureFail,
    clearExportOrdersHistoryFieldErr,
    clearExportOrdersHistoryErrs,
    tryResetExportOrdersHistoryFormErr,
    resetExportedExportOrdersHistory,
    exportOrdersHistoryStart,
    exportOrdersHistoryPass,
    exportOrdersHistoryFail
} = ordersSlice.actions;




interface ITryRegOrderArg {
    data: {
        userIsCustomer?: boolean,
        customerId: string;
        staffId?: string;
        rentalItems: { _id: string, count: string }[];
        siteId?: string;
        deliveryDueDate: string;
        rentalDaysCount: string;
        transportationFee: string;
        paymentTypes: string[];
        bankName: string;
        accountName: string;
        accountNumber: string;
        totalCost?: string;
        discount?: string;
        remarks?: string;
    };
    authToken: string;
    setPaymentLink?: (link: string) => void;
}

export const tryRegOrder = createAsyncThunk('orders/tryRegOrder', (arg: ITryRegOrderArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/order`;

    const { data, authToken, setPaymentLink } = arg;

    dispatch(regOrderStart());

    axios.post(endpoint, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        if (res?.data?.paymentLink && setPaymentLink) {
            setPaymentLink(res?.data?.paymentLink as string);
        }
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(regOrderPass());
    }).catch(err => {

        const data = err?.response?.data;


        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(regOrderFail(fieldErrors));
    })
});

interface ITryUpdateOrderArg {
    orderId: string;
    setPaymentLink?: (link: string) => void;
    data: {
        rentalItems?: { _id: string, count: string }[];
        rentalDaysCount?: string;
        discount?: string;
        transportationFee?: string;
        siteId?: string;
        deliveryDueDate?: string;
        remarks?: string;
        paymentTypes?: string[];
        bankName?: string;
        accountName?: string;
        accountNumber?: string;
        totalCost?: string;
        customerId?: string;
    };
    authToken: string;
}

export const tryUpdateOrder = createAsyncThunk('orders/tryUpdateOrder', (arg: ITryUpdateOrderArg, { dispatch }) => {

    const { orderId, data, authToken, setPaymentLink } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders/${orderId}`;

    dispatch(updateOrderStart());

    axios.put(endpoint, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);

        if (setPaymentLink) {
            setPaymentLink(res.data.paymentLink);
        }

        dispatch(updateOrderPass(res.data));
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(updateOrderFail(fieldErrors));
    });
});

interface ITryFetchRecentOrdersArg {
    authToken: string;
    customerId?: string;
    staffId?: string;
    creatorUserId?: string;
    rentalItemId?: string;
}

export const tryFetchRecentOrders = createAsyncThunk('orders/tryFetchRecentOrders', (arg: ITryFetchRecentOrdersArg, { dispatch }) => {
    const { authToken, customerId, staffId, creatorUserId, rentalItemId } = arg;


    const queryBuilder = new QueryStringBuilder();

    if (customerId) {
        queryBuilder.addInput('customerId', customerId);
    }

    if (staffId) {
        queryBuilder.addInput('staffId', staffId);
    }

    if (creatorUserId) {
        queryBuilder.addInput('creatorUserId', creatorUserId);
    }

    if (rentalItemId) {
        queryBuilder.addInput('rentalItemId', rentalItemId);
    }

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders${queryBuilder.getString()}`;

    dispatch(fetchRecentOrdersStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchRecentOrdersPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(fetchRecentOrdersFail(data.err));
        } else {
            dispatch(fetchRecentOrdersFail('Check your network connection.'));
        }
    })
});

interface ITryFetchUnapprovedOrdersArg {
    authToken: string;
}

export const tryFetchUnapprovedOrders = createAsyncThunk('orders/tryFetchUnapprovedOrders', (arg: ITryFetchUnapprovedOrdersArg, { dispatch }) => {
    const { authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders?status=${UNAPPROVED}`;

    dispatch(fetchUnapprovedOrdersStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchUnapprovedOrdersPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(fetchUnapprovedOrdersFail(data.err));
        } else {
            dispatch(fetchUnapprovedOrdersFail('Check your network connection.'));
        }
    })
});


interface ITryFetchActiveOrdersArg {
    authToken: string;
}

export const tryFetchActiveOrders = createAsyncThunk('orders/tryFetchActiveOrders', (arg: ITryFetchActiveOrdersArg, { dispatch }) => {
    const { authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders?statuses=${APPROVED},${INITIALIZED}`;

    dispatch(fetchActiveOrdersStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchActiveOrdersPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(fetchActiveOrdersFail(getFieldErrors(data.errors)[0].msg));
        } else if (data?.err) {
            dispatch(fetchActiveOrdersFail(data.err));
        } else {
            dispatch(fetchActiveOrdersFail('Check your network connection.'));
        }
    })
});

interface ITryFetchCompletedOrdersArg {
    authToken: string;
}

export const tryFetchCompletedOrders = createAsyncThunk('orders/tryFetchCompletedOrders', (arg: ITryFetchCompletedOrdersArg, { dispatch }) => {
    const { authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders?status=${COMPLETED}`;

    dispatch(fetchCompletedOrdersStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchCompletedOrdersPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(fetchCompletedOrdersFail(getFieldErrors(data.errors)[0].msg));
        } else if (data?.err) {
            dispatch(fetchCompletedOrdersFail(data.err));
        } else {
            dispatch(fetchCompletedOrdersFail('Check your network connection.'));
        }
    })
});

interface ITryFetchOrders4DeliveryArg {
    authToken: string;
    deliveryDriverId?: string;
    statuses?: string;      //csv
}

export const tryFetchOrders4Delivery = createAsyncThunk('orders/tryFetchOrders4Delivery', (arg: ITryFetchOrders4DeliveryArg, { dispatch }) => {
    const { authToken, deliveryDriverId, statuses } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders?${statuses ? `statuses=${statuses}` : `status=${APPROVED}`}${deliveryDriverId ? `&deliveryDriverId=${deliveryDriverId}` : ``}`;

    dispatch(fetchOrders4DeliveryStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchOrders4DeliveryPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(fetchOrders4DeliveryFail(getFieldErrors(data.errors)[0].msg));
        } else if (data?.err) {
            dispatch(fetchOrders4DeliveryFail(data.err));
        } else {
            dispatch(fetchOrders4DeliveryFail('Check your network connection.'));
        }
    })
});

interface ITryFetchOrders4PickUpArg {
    authToken: string;
    pickUpDriverId?: string;
    statuses?: string;      //csv
}

export const tryFetchOrders4PickUp = createAsyncThunk('orders/tryFetchOrders4PickUp', (arg: ITryFetchOrders4PickUpArg, { dispatch }) => {
    const { authToken, pickUpDriverId, statuses } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders?${statuses ? `statuses=${statuses}` : `status=${INITIALIZED}`}${pickUpDriverId ? `&retrievalDriverId=${pickUpDriverId}` : ``}`;

    dispatch(fetchOrders4PickUpStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchOrders4PickUpPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(fetchOrders4PickUpFail(getFieldErrors(data.errors)[0].msg));
        } else if (data?.err) {
            dispatch(fetchOrders4PickUpFail(data.err));
        } else {
            dispatch(fetchOrders4PickUpFail('Check your network connection.'));
        }
    })
});

interface ITryFetchMainOrderArg {
    authToken: string;
    orderId: string;
}

export const tryFetchMainOrder = createAsyncThunk('orders/tryFetchMainOrder', (arg: ITryFetchMainOrderArg, { dispatch }) => {
    const { authToken, orderId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders/${orderId}`;

    dispatch(fetchMainOrderStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        const data = res.data;

        dispatch(fetchMainOrderPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(fetchMainOrderFail(data.err));
        } else {
            dispatch(fetchMainOrderFail('Check your network connection.'));
        }
    });
});

interface ITryFetchActiveMembershipArg {
    authToken: string;
}

export const tryFetchActiveMembership = createAsyncThunk('orders/tryFetchActiveMembership', (arg: ITryFetchActiveMembershipArg, { dispatch }) => {
    const { authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/main-order?rentalItemType=${MEMBERSHIP}`;

    dispatch(fetchActiveMembershipStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        const data = res.data;

        dispatch(fetchActiveMembershipPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(fetchActiveMembershipFail(data.err));
        } else {
            dispatch(fetchActiveMembershipFail('Check your network connection.'));
        }
    });
});

interface ITryApproveOrderArg {
    authToken: string;
    orderId: string;
    setApproved: (truth: boolean) => void;
}

export const tryApproveOrder = createAsyncThunk('orders/tryApproveOrder', (arg: ITryApproveOrderArg, { dispatch }) => {

    const { authToken, orderId, setApproved } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders/${orderId}/approve`;

    dispatch(approveOrderStart());
    axios.post(endpoint, {}, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(approveOrderPass());

        setApproved(true);
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(approveOrderFail(getFieldErrors(data.errors)[0].msg))
        } else if (data?.err) {
            dispatch(approveOrderFail(data.err));
        } else {
            dispatch(approveOrderFail('Check your network connection.'));
        }
    });
});

interface ITryDeleteOrderArg {
    authToken: string;
    orderId: string;
}

export const tryDeleteOrder = createAsyncThunk('orders/tryDeleteOrder', (arg: ITryDeleteOrderArg, { dispatch }) => {
    const { authToken, orderId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders/${orderId}`;

    dispatch(deleteOrderStart());
    axios.delete(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(deleteOrderPass())
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(deleteOrderFail(getFieldErrors(data.errors)[0].msg))
        } else if (data?.err) {
            dispatch(deleteOrderFail(data.err));
        } else {
            dispatch(deleteOrderFail('Check your network connection.'));
        }
    })
});

interface IAssignDeliveryDriverArg {
    authToken: string;
    orderId: string;
    driverId: string;
}

export const tryAssignDeliveryDriver = createAsyncThunk('orders/tryAssignDeliveryDriver', (arg: IAssignDeliveryDriverArg, { dispatch }) => {

    const { authToken, orderId, driverId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders/${orderId}/assign-delivery-driver`;

    dispatch(assignDeliveryDriverStart());

    axios.post(endpoint, { driverId }, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(assignDeliveryDriverPass());

    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(assignDeliveryDriverFail(getFieldErrors(data.errors)[0].msg))
        } else if (data?.err) {
            dispatch(assignDeliveryDriverFail(data.err));
        } else {
            dispatch(assignDeliveryDriverFail('Check your network connection.'));
        }
    });

});

interface IAssignRetrievalDriverArg {
    authToken: string;
    orderId: string;
    driverId: string;
}

export const tryAssignRetrievalDriver = createAsyncThunk('orders/tryAssignRetrievalDriver', (arg: IAssignRetrievalDriverArg, { dispatch }) => {

    const { authToken, orderId, driverId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders/${orderId}/assign-retrieval-driver`;

    dispatch(assignRetrievalDriverStart());

    axios.post(endpoint, { driverId }, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(assignRetrievalDriverPass());

    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(assignRetrievalDriverFail(getFieldErrors(data.errors)[0].msg))
        } else if (data?.err) {
            dispatch(assignRetrievalDriverFail(data.err));
        } else {
            dispatch(assignRetrievalDriverFail('Check your network connection.'));
        }
    });

});

interface ITryConfirmOrderCompletionArg {
    authToken: string;
    orderId: string;
}

export const tryConfirmOrederCompletion = createAsyncThunk('orders/tryConfirmOrderCompletion', (arg: ITryConfirmOrderCompletionArg, { dispatch }) => {
    const { authToken, orderId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders/${orderId}/confirm-completion`;

    dispatch(confirmOrderCompletionStart());

    axios.post(endpoint, {}, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(confirmOrderCompletionPass());

    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(confirmOrderCompletionFail(getFieldErrors(data.errors)[0].msg))
        } else if (data?.err) {
            dispatch(confirmOrderCompletionFail(data.err));
        } else {
            dispatch(confirmOrderCompletionFail('Check your network connection.'));
        }
    });
});

interface ITryConfirmOrderDepartureArg {
    authToken: string;
    orderId: string;
}

export const tryConfirmOrederDeparture = createAsyncThunk('orders/tryConfirmOrderDeparture', (arg: ITryConfirmOrderDepartureArg, { dispatch }) => {
    const { authToken, orderId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders/${orderId}/confirm-departure`;

    dispatch(confirmOrderDepartureStart());

    axios.post(endpoint, {}, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(confirmOrderDeparturePass());

    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(confirmOrderDepartureFail(getFieldErrors(data.errors)[0].msg))
        } else if (data?.err) {
            dispatch(confirmOrderDepartureFail(data.err));
        } else {
            dispatch(confirmOrderDepartureFail('Check your network connection.'));
        }
    });
});


export interface IData4TryExportOrdersHistory {
    startTimeStamp?: string;
    endTimeStamp?: string;
    creatorUserId?: string;
    customerId?: string;
    staffId?: string;
    deliveryDriverId?: string;
    retrievalDriverId?: string;
}

interface ITryExportOrdersHistoryArg {
    format: string;
    authToken: string;
    data: IData4TryExportOrdersHistory;
}

export const tryExportOrdersHistory = createAsyncThunk("orders/tryExportOrdersHistory", (arg: ITryExportOrdersHistoryArg, { dispatch }) => {

    const { authToken, format, data } = arg;

    const queryBuilder = new QueryStringBuilder();

    for (const key in data) {
        if ((data[key as keyof typeof data] !== undefined) && (data[key as keyof typeof data] !== "")) {
            queryBuilder.addInput(key, data[key as keyof typeof data] as string);
        }
    }

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders-history${queryBuilder.getString()}`;

    dispatch(exportOrdersHistoryStart());
    axios.get(endpoint, {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`
        }
    }).then(res => {
        const data = res.data;

        if (data.length === 0) {
            const fieldErrors = [{ field: 'form', msg: 'Export Empty.' }];

            dispatch(exportOrdersHistoryFail(fieldErrors));
        } else {

            switch (format) {
                case XLSX:
                default:
                    const fileSettings = {
                        fileName: "rental_orders"        //TODO: dynamically generate the name, based on the parameters used to fetch the data
                    };

                    try {
                        xlsx(interpretDataIntoXlsxFormat(data), fileSettings);
                    } catch (err) {
                        alert("err");
                        console.log(err);
                    }
                    break;
            }

            dispatch(exportOrdersHistoryPass());
        }
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            console.log(data);
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(exportOrdersHistoryFail(fieldErrors));
    })


});

export default ordersSlice.reducer